import { useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  EmptyState,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  Switch,
  Text,
} from "@salestrak/ui";
import {
  cellEditable,
  PERMISSION_REGISTRY,
  SYSTEM_ROLE_NAMES,
  type CellState,
} from "@salestrak/permissions";
import { PermissionsSection } from "#app/components/settings/permissions/permissions-section";
import {
  MatrixCard,
  MatrixRow,
  MatrixLead,
  MatrixCell,
  PermissionsMatrixSkeleton,
  RoleColumnHeader,
  matrixRowMinWidth,
} from "#app/components/settings/permissions/matrix-chrome";
import { NewRoleDialog } from "#app/components/settings/new-role-dialog";
import { usePermissionMatrix } from "#app/lib/hooks/use-permission-matrix";

// ─── Custom Role Permissions ──────────────────────────────────────────
// Role × permission-key matrix. Rows are grouped by registry category so
// the table reads like a settings page rather than a flat grid. Pulls
// `permissionMatrix` and writes via `setRolePermission` — both already
// guarded by `create.roles` on the resolver.

// Empty string = render the group without a section header row. The
// "create_delete" bucket (Create / Delete / Archive Items) shows up
// inline like a continuation of the section above it.
const CATEGORY_LABELS: Record<string, string> = {
  manage: "MANAGE",
  workflows: "WORKFLOWS",
  create_delete: "",
  custom_fields: "CUSTOM FIELDS",
  forms: "FORMS",
  comments: "COMMENTS",
  collaboration: "COLLABORATION",
};

// Order categories in the display the same way the registry lists them
// rather than alphabetically. `workspace`-category keys (issue_types /
// reasons / trash manage) are deliberately absent — backend-only for
// now, not editable from the matrix UI.
const CATEGORY_ORDER: readonly string[] = [
  "manage",
  "custom_fields",
  "forms",
  "workflows",
  "create_delete",
  "comments",
  "collaboration",
];

// Show Owner-less columns to match the invite matrix (Owner is all-locked
// yes and adds no decision).
const HIDDEN_ROLE_NAMES = new Set([SYSTEM_ROLE_NAMES.owner]);

// The settled matrix's row count is knowable before the query returns: the
// server serves this same registry filtered to CATEGORY_ORDER, and the grid
// adds one category-header row per labeled group after the first — so the
// skeleton reserves the exact settled run instead of a token few rows.
const MATRIX_GROUPS = CATEGORY_ORDER.map((category) => ({
  category,
  keyCount: PERMISSION_REGISTRY.filter((p) => p.category === category).length,
})).filter((g) => g.keyCount > 0);
const MATRIX_SKELETON_ROWS =
  MATRIX_GROUPS.reduce((sum, g) => sum + g.keyCount, 0) +
  MATRIX_GROUPS.filter(
    (g, gi) => gi > 0 && (CATEGORY_LABELS[g.category] ?? "").length > 0,
  ).length;

export function CustomRolePermissionsSection({
  workspaceId,
  onRenameRole,
  onDeleteRole,
}: {
  workspaceId: string;
  onRenameRole: (role: { id: string; roleName: string }) => void;
  onDeleteRole: (role: { id: string; roleName: string }) => void;
}) {
  const { loading, matrix, setCell } = usePermissionMatrix(workspaceId);
  const [search, setSearch] = useState("");
  const [showNewRole, setShowNewRole] = useState(false);

  const roles = useMemo(
    () =>
      (matrix?.roles ?? []).filter(
        (r) => !HIDDEN_ROLE_NAMES.has(r.roleName),
      ),
    [matrix],
  );
  const keys = useMemo(() => matrix?.keys ?? [], [matrix]);

  // `${roleId}:${permissionKey}` → cell state.
  const stateByCell = useMemo(() => {
    const map = new Map<string, CellState>();
    for (const c of matrix?.cells ?? []) {
      map.set(`${c.roleId}:${c.permissionKey}`, c.state as CellState);
    }
    return map;
  }, [matrix]);

  // Substring filter against label + description so the admin can
  // narrow the matrix to a single rule without scrolling. Case-
  // insensitive; empty query keeps the full grouped list.
  const filteredKeys = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return keys;
    return keys.filter((k) => {
      const label = (k.label ?? "").toLowerCase();
      const desc = (k.description ?? "").toLowerCase();
      return label.includes(q) || desc.includes(q);
    });
  }, [keys, search]);

  const keysByCategory = useMemo(() => {
    const grouped = new Map<string, typeof filteredKeys>();
    for (const k of filteredKeys) {
      const arr = grouped.get(k.category) ?? [];
      arr.push(k);
      grouped.set(k.category, arr);
    }
    return CATEGORY_ORDER.filter((c) => grouped.has(c)).map((c) => ({
      category: c,
      keys: grouped.get(c)!,
    }));
  }, [filteredKeys]);

  return (
    <PermissionsSection icon="lock" title="Custom Role Permissions">
      {/* The search scopes the matrix below it, so the pair sits closer than
          the section rhythm — the project-types search/card spacing. */}
      <Box gap="base">
        {/* Toolbar — search filters the matrix in place; "+ New role"
            opens the dialog. Always render so the user can start typing
            before the matrix finishes loading. */}
        <Box direction="row" align="center" gap="sm">
          <Box flex={1}>
            {/* The search that scopes the matrix runs at the `xl` rung on the
                page fill (`outline` — the default variant's dark tint reads as
                a form well), the project-types treatment. The row's action
                takes the matching Button rung: both are 40px desktop / 44px
                touch, so the pair sits on one line. */}
            <InputGroup variant="outline">
              <InputGroupAddon>
                <Icon name="search" />
              </InputGroupAddon>
              <InputGroupInput
                size="xl"
                type="search"
                placeholder="Search for action..."
                value={search}
                onValueChange={setSearch}
              />
            </InputGroup>
          </Box>
          <Button size="xl" variant="outline" onClick={() => setShowNewRole(true)}>
            <Icon name="plus" size="lg" />
            <Text>New Role</Text>
          </Button>
        </Box>

        {loading && !matrix ? (
          <PermissionsMatrixSkeleton rows={MATRIX_SKELETON_ROWS} />
        ) : roles.length === 0 || keys.length === 0 ? (
          <EmptyState size="xs" title="No roles found." />
        ) : (
          <MatrixCard>
            {/* Header — one tonal step below the card (`muted` fill, no
                re-declared surface): re-naming `card` here would repaint the
                container's own fill and stack a second card shadow, and the
                hard hairline under it read as a heavy table seam. */}
            <MatrixRow background="muted" columns={roles.length}>
              <MatrixLead justify="center" paddingY="sm">
                <Text size="xs" variant="muted" weight="semibold">
                  ACTIONS
                </Text>
              </MatrixLead>
              {roles.map((role, i) => (
                <MatrixCell key={`${role.id}-${i}`} first={i === 0} paddingY="sm">
                  <RoleColumnHeader
                    role={role}
                    onRename={onRenameRole}
                    onDelete={onDeleteRole}
                  />
                </MatrixCell>
              ))}
            </MatrixRow>

            {keysByCategory.map((group, gi) => (
              // The group's own divider spans the scroll width, not the
              // card's — a container-width border would stop short of the
              // columns scrolled into view.
              <Box
                key={`${group.category}-${gi}`}
                border={gi < keysByCategory.length - 1 ? "bottom" : undefined}
                minWidth={matrixRowMinWidth(roles.length)}
              >
                {/* Category header — subtle background tint, no divider
                    beneath so it reads as a label sitting above its rows.
                    Skipped entirely when the label is empty (the bucket
                    flows inline with the section above). */}
                {gi > 0 && (CATEGORY_LABELS[group.category] ?? "").length > 0 ? (
                  <MatrixRow columns={roles.length}>
                    <MatrixLead paddingY="lg">
                      <Text size="xs" variant="muted" weight="semibold">
                        {CATEGORY_LABELS[group.category] ??
                          group.category.toUpperCase()}
                      </Text>
                    </MatrixLead>
                    {roles.map((role, ci) => (
                      <MatrixCell key={`${role.id}-cat-${ci}`} first={ci === 0} />
                    ))}
                  </MatrixRow>
                ) : null}
                {group.keys.map((perm, pi) => (
                  <MatrixRow key={`${perm.key}-${pi}`} columns={roles.length}>
                    <MatrixLead gap="2xs">
                      <Text size="sm" weight="semibold">
                        {perm.label}
                      </Text>
                      <Text size="xs" variant="muted">
                        {perm.description}
                      </Text>
                    </MatrixLead>
                    {roles.map((role, ci) => {
                      const state =
                        stateByCell.get(`${role.id}:${perm.key}`) ?? "no";
                      const editable = cellEditable(state);
                      // Toggle reads as "on" for any granting state, including
                      // if_created (which grants conditionally on ownership).
                      const granted =
                        state === "yes" ||
                        state === "yes_locked" ||
                        state === "if_created";
                      const showIfCreated =
                        !!perm.supportsOnlyIfCreated && editable && granted;
                      return (
                        <MatrixCell key={`${role.id}-${ci}`} first={ci === 0}>
                          {!editable ? (
                            granted ? (
                              <Icon name="check" size="sm" color="muted" />
                            ) : (
                              <Icon name="x" size="sm" color="muted" />
                            )
                          ) : (
                            <Switch
                              aria-label={`${perm.label}: ${role.roleName}`}
                              checked={granted}
                              onCheckedChange={(next) =>
                                setCell(
                                  role.id,
                                  perm.key,
                                  next
                                    ? state === "if_created"
                                      ? "if_created"
                                      : "yes"
                                    : "no",
                                )
                              }
                            />
                          )}
                          {showIfCreated ? (
                            // Checkbox has no label slot; wrap it in a label so
                            // the text is part of the hit target.
                            <Box
                              as="label"
                              direction="row"
                              align="center"
                              gap="xs"
                              cursor="pointer"
                            >
                              <Checkbox
                                checked={state === "if_created"}
                                onCheckedChange={(next) =>
                                  setCell(
                                    role.id,
                                    perm.key,
                                    next ? "if_created" : "yes",
                                  )
                                }
                              />
                              <Text size="xs" variant="muted" truncate={1}>
                                Only if created
                              </Text>
                            </Box>
                          ) : null}
                        </MatrixCell>
                      );
                    })}
                  </MatrixRow>
                ))}
              </Box>
            ))}
          </MatrixCard>
        )}
      </Box>
      <NewRoleDialog
        open={showNewRole}
        onOpenChange={setShowNewRole}
        workspaceId={workspaceId}
      />
    </PermissionsSection>
  );
}
